/**
 * Hashes a string using the SHA-256 algorithm.
 * @param {string} inputString The string to hash.
 * @returns {Promise<string>} The hex-encoded hash.
 */
export async function sha256(inputString) {
  // Convert the string to a Uint8Array
  const encoder = new TextEncoder();
  const data = encoder.encode(inputString);

  // Hash the data
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);

  // Convert the buffer to a hex string
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');

  return hashHex;
}

//process.env.REACT_APP_BUYING_HOUSE_DOMAIN_BASE_URL

// Function to generate a new encryption key and store it
export async function generateKey() {
  const key = await window.crypto.subtle.generateKey(
    {
      name: "AES-GCM",
      length: 256,
    },
    true, // Can extract the key
    ["encrypt", "decrypt"]
  );
  const exportedKey = await window.crypto.subtle.exportKey("jwk", key);
  console.log("exportedKey", JSON.stringify(exportedKey));
  return {
    exportedKey,
    key
  };
}

// Function to retrieve the stored key
export async function getKey() {
    const k = process.env.REACT_APP_ENCRYPTION_KEY;
    const t = `{"alg":"A256GCM","ext":true,"k":"${k}","key_ops":["encrypt","decrypt"],"kty":"oct"}`
    const storedKeyJwk = JSON.parse(t);

    if (!storedKeyJwk) {
        throw new Error("No key is provided");
    }
    return window.crypto.subtle.importKey(
        "jwk",
        storedKeyJwk,
        {
            name: "AES-GCM"
        },
        true,
        ["encrypt", "decrypt"]
    );
}

export async function encryptData(data) {
  const key = await getKey();
  const encoder = new TextEncoder();
  const encodedData = encoder.encode(data);
  // Generate a unique Initialization Vector (IV) for each encryption
  const iv = window.crypto.subtle.generateKey({name: "AES-GCM", length: 96}, false, []); // 96 bits = 12 bytes
  const encrypted = await window.crypto.subtle.encrypt(
    {
      name: "AES-GCM",
      iv: iv,
    },
    key,
    encodedData
  );

  // Combine IV and encrypted data into a single string for storage/transmission
  const fullArray = new Uint8Array(iv.byteLength + encrypted.byteLength);
  fullArray.set(new Uint8Array(iv), 0);
  fullArray.set(new Uint8Array(encrypted), iv.byteLength);

  return btoa(String.fromCharCode.apply(null, fullArray)); // Convert to base64
}

// Function to decrypt data (reverse the "hash")
export async function decryptData(encryptedBase64) {
  const key = await getKey();
  const decodedData = Uint8Array.from(atob(encryptedBase64), c => c.charCodeAt(0));
  const iv = decodedData.slice(0, 12); // Extract the IV (first 12 bytes)
  const encryptedBytes = decodedData.slice(12);

  const decrypted = await window.crypto.subtle.decrypt(
    {
      name: "AES-GCM",
      iv: iv,
    },
    key,
    encryptedBytes
  );
  const decoder = new TextDecoder();

  return decoder.decode(decrypted);
}

// Function to encrypt data using AES-GCM and format for URL
export async function encryptAndFormat(dataToEncrypt, sharedKey) {
  const ec = new TextEncoder();
  const data = ec.encode(dataToEncrypt);
  const iv = window.crypto.getRandomValues(new Uint8Array(12)); // GCM standard IV size
  
  // Import the key (assuming 'sharedKey' is a raw ArrayBuffer/Uint8Array key)
  const cryptoKey = await window.crypto.subtle.importKey(
    'raw',
    sharedKey,
    { name: 'AES-GCM' },
    false,
    ['encrypt']
  );

  const encrypted = await window.crypto.subtle.encrypt(
    { name: 'AES-GCM', iv: iv },
    cryptoKey,
    data
  );

  // Combine IV and encrypted data and convert to Base64 (URL-safe)
  const combined = new Uint8Array(iv.length + encrypted.byteLength);
  combined.set(iv);
  combined.set(new Uint8Array(encrypted), iv.length);

  // Use btoa for Base64 encoding. Replace '+' and '/' for URL safety.
  const base64String = btoa(String.fromCharCode.apply(null, combined))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, ''); // Remove padding

  return base64String;
}

// Example usage:
// const encryptedData = await encryptAndFormat('sensitive data here', mySecretKey);
// const emailLink = `https://yourdomain.com{encryptedData}`;
// Send this link in an email.

// Function to decrypt data
export async function decryptFormatted(formattedData, sharedKey) {
  // Reverse URL-safe Base64 decode
  let base64 = formattedData.replace(/-/g, '+').replace(/_/g, '/');
  while (base64.length % 4) base64 += '='; // Re-add padding
  
  const rawData = Uint8Array.from(atob(base64), c => c.charCodeAt(0));
  
  // Split IV and encrypted data
  const iv = rawData.slice(0, 12);
  const encrypted = rawData.slice(12);

  // Import the same key
  const cryptoKey = await window.crypto.subtle.importKey(
    'raw',
    sharedKey,
    { name: 'AES-GCM' },
    false,
    ['decrypt']
  );

  const decryptedBuffer = await window.crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: iv },
    cryptoKey,
    encrypted
  );

  const decoder = new TextDecoder();
  return decoder.decode(decryptedBuffer);
}
